import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { DatePicker } from '../ui/date-picker';
import { parseISO, format } from 'date-fns';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';

interface RowEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (row: any) => void;
  row: any;
  columns: any[];
  title?: string;
  allComponents?: any[];
}

export default function RowEditModal({
  isOpen,
  onClose,
  onSave,
  row,
  columns,
  title = 'Editar Linha',
  allComponents = [],
}: RowEditModalProps) {
  const [editedRow, setEditedRow] = useState<any>({});

  useEffect(() => {
    setEditedRow({ ...row });
  }, [row, isOpen]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(editedRow);
    onClose();
  };

  const visibleColumns = columns.filter((col) => col.visible !== false);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
          <div className="grid grid-cols-2 gap-4 py-4 overflow-y-auto pr-2">
            {visibleColumns
              .filter((col: any) => col.field.toLowerCase() !== 'id' && col.type !== 'id')
              .map((col: any) => {
                // Para colunas lookup, renderizar Select
                if (col.type === 'lookup' && col.lookupTable) {
                  const lookupComponent = allComponents.find(
                    (c: any) => c.id === col.lookupTable.tableId
                  );
                  const lookupData = lookupComponent?.config?.dataSource?.data || [];
                  
                  return (
                    <div key={col.field} className="space-y-2">
                      <Label htmlFor={col.field}>{col.label}</Label>
                      <Select
                        value={editedRow[col.field]?.toString() || ''}
                        onValueChange={(value) => {
                          setEditedRow({
                            ...editedRow,
                            [col.field]: value,
                          });
                        }}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Selecione..." />
                        </SelectTrigger>
                        <SelectContent>
                          {lookupData.map((item: any) => {
                            const keyValue = item[col.lookupTable.keyField];
                            const displayValue = item[col.lookupTable.displayField];
                            return (
                              <SelectItem key={keyValue} value={keyValue?.toString()}>
                                {displayValue}
                              </SelectItem>
                            );
                          })}
                        </SelectContent>
                      </Select>
                    </div>
                  );
                }
                
                // Para colunas tableLink, renderizar dois inputs: Nome e URL
                if (col.type === 'tableLink') {
                  const val = editedRow[col.field];
                  const parsed = val && typeof val === 'object' ? val : {};
                  return (
                    <div key={col.field} className="col-span-2 space-y-2">
                      <Label>{col.label}</Label>
                      <Input
                        type="text"
                        placeholder="Nome"
                        value={parsed.name || ''}
                        onChange={(e) =>
                          setEditedRow({ ...editedRow, [col.field]: { ...parsed, name: e.target.value } })
                        }
                      />
                      <Input
                        type="text"
                        placeholder="URL do Google Sheets"
                        value={parsed.sheetsUrl || ''}
                        onChange={(e) =>
                          setEditedRow({ ...editedRow, [col.field]: { ...parsed, sheetsUrl: e.target.value } })
                        }
                        disabled={!!parsed.pageSlug}
                      />
                      {parsed.pageSlug && (
                        <p className="text-xs text-muted-foreground">
                          Página criada:{' '}
                          <a href={`/pages/${parsed.pageSlug}`} className="underline text-primary">
                            /pages/{parsed.pageSlug}
                          </a>
                        </p>
                      )}
                    </div>
                  );
                }

                // Para outros tipos de coluna, renderizar Input
                return (
                  <div key={col.field} className="space-y-2">
                    <Label htmlFor={col.field}>{col.label}</Label>
                    {col.type === 'date' ? (
                      (() => {
                        // Parse date from various formats
                        let parsedDate: Date | undefined;
                        const value = editedRow[col.field];
                        if (value) {
                          const stringValue = value.toString().trim();
                          // Check if Brazilian format (dd/mm/yyyy, dd-mm-yyyy, dd.mm.yyyy)
                          const brDatePattern = /^\d{1,2}[\/\-\.]\d{1,2}[\/\-\.]\d{2,4}$/;
                          if (brDatePattern.test(stringValue)) {
                            const parts = stringValue.split(/[\/\-\.]/);
                            const day = parseInt(parts[0]);
                            const month = parseInt(parts[1]) - 1;
                            const year = parseInt(parts[2]);
                            parsedDate = new Date(year, month, day);
                          } else {
                            // Try ISO or other formats
                            try {
                              parsedDate = parseISO(stringValue);
                            } catch {
                              parsedDate = undefined;
                            }
                          }
                        }
                        return (
                          <DatePicker
                            date={parsedDate}
                            onDateChange={(date) => setEditedRow({ ...editedRow, [col.field]: date ? format(date, "yyyy-MM-dd") : "" })}
                            placeholder="Selecione a data"
                          />
                        );
                      })()
                    ) : (
                      <Input
                        id={col.field}
                        type={col.type === 'number' ? 'number' : 'text'}
                        value={editedRow[col.field] || ''}
                        onChange={(e) => {
                          setEditedRow({
                            ...editedRow,
                            [col.field]: e.target.value,
                          });
                        }}
                      />
                    )}
                  </div>
                );
              })}
          </div>

          <DialogFooter className="mt-4">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancelar
            </Button>
            <Button type="submit">Salvar</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
